import React, { useEffect, useState } from "react";
import { getDriverAllowanceStatement } from "../../api/apiGetrequest";

const details = JSON.parse(localStorage.getItem("user"));

const AllowanceSummaryPage = () => {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(
    thirtyDaysAgo.toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);
  const [statements, setStatements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatements();
  }, [startDate, endDate]);

  // Updated safeFormat function for accounting format with commas
  const safeFormat = (value, places = 2) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "0.00";
    }
    return Number(value).toLocaleString("en-KE", {
      minimumFractionDigits: places,
      maximumFractionDigits: places,
    });
  };

  const fetchStatements = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getDriverAllowanceStatement(
        details.data.userID,
        startDate,
        endDate
      );

      if (response && response.result && Array.isArray(response.result)) {
        const normalizedStatements = response.result.map((item) => ({
          transactionDate: item.TransactionDate || new Date().toISOString(),
          description: item.Description || "No description",
          debit: item.Debit || 0,
          credit: item.Credit || 0,
          balance: item.Balance || 0,
        }));
        setStatements(normalizedStatements);
      } else {
        setStatements([]);
      }
    } catch (error) {
      console.error("Error fetching statement:", error);
      setError("Failed to load statement. Please try again.");
      setStatements([]);
    } finally {
      setIsLoading(false);
    }
  };

  const totals = statements.reduce(
    (acc, curr) => ({
      debit: acc.debit + (curr.debit || 0),
      credit: acc.credit + (curr.credit || 0),
      balance: curr.balance || 0,
    }),
    { debit: 0, credit: 0, balance: 0 }
  );
  const lastStatement = statements[statements.length - 1];
  totals.balance = lastStatement ? lastStatement.balance : 0;

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }
        
        body {
          margin: 0;
          padding: 0;
          background-color: #f8f9fa;
          color: #333;
        }
        
        .container {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }
        
        .page-title {
          font-size: 24px;
          color: #2c3e50;
          margin-bottom: 20px;
          border-bottom: 2px solid #eaeaea;
          padding-bottom: 10px;
        }
        
        .date-controls {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
          margin-bottom: 20px;
          background-color: #f5f7f9;
          padding: 15px;
          border-radius: 6px;
          align-items: center;
        }
        
        .date-input-group {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 150px;
        }
        
        .date-label {
          font-weight: 600;
          margin-bottom: 5px;
          color: #445566;
          font-size: 14px;
        }
        
        .date-input {
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
        }
        
        .date-input:focus {
          outline: none;
          border-color: #4a90e2;
          box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.25);
        }
        
        .refresh-button {
          height: 36px;
          background-color: #4a90e2;
          color: white;
          border: none;
          border-radius: 4px;
          padding: 0 12px;
          cursor: pointer;
          font-weight: 600;
          font-size: 14px;
          transition: background-color 0.2s;
          align-self: flex-end;
        }
        
        .refresh-button:hover {
          background-color: #3a7bc8;
        }
        
        .table-wrapper {
          max-height: 400px;
          overflow-y: auto;
          position: relative;
          margin-bottom: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        
        .statement-table {
          width: 100%;
          border-collapse: collapse;
          background-color: white;
        }
        
        .statement-table th {
          background-color: #445566;
          color: white;
          padding: 10px;
          font-weight: 600;
          font-size: 14px;
          position: sticky;
          top: 0;
          z-index: 1;
        }
        
        .statement-table th.numeric-header {
          text-align: right;
        }
        
        .statement-table td {
          padding: 10px;
          border-bottom: 1px solid #eaeaea;
          font-size: 13px;
        }
        
        .statement-table tr:nth-child(even) {
          background-color: #f9f9f9;
        }
        
        .statement-table tr:hover {
          background-color: #f0f4f8;
        }
        
        .totals-row td {
          border-top: 3px solid #445566;
          font-weight: bold;
          background-color: #f5f7f9;
          padding: 12px 10px;
        }
        
        .loading {
          display: flex;
          justify-content: center;
          padding: 20px;
          font-size: 16px;
          color: #666;
        }
        
        .error {
          background-color: #ffebee;
          color: #e53935;
          padding: 10px 15px;
          border-radius: 4px;
          margin-bottom: 20px;
          border-left: 4px solid #e53935;
          font-size: 14px;
        }
        
        .numeric-cell {
          text-align: right;
          font-family: monospace;
          font-size: 13px;
        }
        
        .positive {
          color: #27ae60;
        }
        
        .negative {
          color: #e74c3c;
        }
        
        @media (max-width: 768px) {
          .container {
            padding: 15px;
            border-radius: 0;
            box-shadow: none;
          }
          
          .page-title {
            font-size: 20px;
            margin-bottom: 15px;
          }
          
          .date-controls {
            flex-direction: column;
            align-items: stretch;
            padding: 10px;
            gap: 10px;
          }
          
          .date-input-group {
            min-width: 100%;
          }
          
          .refresh-button {
            align-self: stretch;
            padding: 10px;
          }
          
          .table-wrapper {
            max-height: 300px;
            overflow-x: auto;
            white-space: nowrap;
            -webkit-overflow-scrolling: touch;
          }
          
          .statement-table th,
          .statement-table td {
            min-width: 120px;
            padding: 8px;
          }
          
          .statement-table th {
            font-size: 12px;
          }
          
          .statement-table td {
            font-size: 12px;
          }
          
          .totals-row td {
            padding: 10px 8px;
          }
        }
        
        @media (max-width: 480px) {
          .statement-table th,
          .statement-table td {
            min-width: 100px;
            padding: 6px;
          }
          
          .totals-row td {
            padding: 8px 6px;
          }
        }
      `}</style>

      <div className="container">
        <h1 className="page-title">Driver Allowance Statement</h1>

        <div className="date-controls">
          <div className="date-input-group">
            <label className="date-label">Start Date:</label>
            <input
              className="date-input"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="date-input-group">
            <label className="date-label">End Date:</label>
            <input
              className="date-input"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </div>

        {error && <div className="error">{error}</div>}

        {isLoading ? (
          <div className="loading">Loading statement...</div>
        ) : (
          <>
            {statements && statements.length > 0 ? (
              <div className="table-wrapper">
                <table className="statement-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Description</th>
                      <th className="numeric-header">Debit</th>
                      <th className="numeric-header">Credit</th>
                      <th className="numeric-header">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {statements.map((statement, index) => {
                      let formattedDate = "N/A";
                      try {
                        if (statement.transactionDate) {
                          formattedDate = new Date(
                            statement.transactionDate
                          ).toLocaleDateString();
                        }
                      } catch (e) {
                        console.error("Date formatting error", e);
                      }

                      return (
                        <tr key={index}>
                          <td>{formattedDate}</td>
                          <td>{statement.description || "No description"}</td>
                          <td className="numeric-cell negative">
                            {(statement.debit || 0) > 0
                              ? `KES${safeFormat(statement.debit)}`
                              : "-"}
                          </td>
                          <td className="numeric-cell positive">
                            {(statement.credit || 0) > 0
                              ? `KES${safeFormat(statement.credit)}`
                              : "-"}
                          </td>
                          <td
                            className={`numeric-cell ${
                              (statement.balance || 0) >= 0
                                ? "positive"
                                : "negative"
                            }`}
                          >
                            KES{safeFormat(statement.balance)}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="totals-row">
                      <td colSpan={2}>Totals</td>
                      <td className="numeric-cell negative">
                        {totals.debit > 0
                          ? `KES${safeFormat(totals.debit)}`
                          : "-"}
                      </td>
                      <td className="numeric-cell positive">
                        {totals.credit > 0
                          ? `KES${safeFormat(totals.credit)}`
                          : "-"}
                      </td>
                      <td
                        className={`numeric-cell ${
                          totals.balance >= 0 ? "positive" : "negative"
                        }`}
                      >
                        KES{safeFormat(totals.balance)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  textAlign: "center",
                  padding: "20px 0",
                  color: "#666",
                  fontSize: "14px",
                }}
              >
                No statement data available for the selected date range.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default AllowanceSummaryPage;